import { identity, last, slice, uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, Redirect, useParams, useRouteMatch } from 'react-router';
import { itemHasContent } from '../../../data/filters/directory/itemHasContent';
import { useDirectoryItem } from '../../../data/hooks/directory/directoryHooks';
import { useEvent } from '../../../data/hooks/eventHooks';
import { isLocalLink } from '../../../misc/isLocalLink';
import { openLink } from '../../../misc/openLinks';
import Icon from '../../elements/icons/Icon';
import MaximizeToolbarIcon from '../../elements/MaximizeToolbarIcon';
import Modal from '../../elements/Modal/Modal';
import Scrollable from '../../elements/Scrollable';
import TopBarEvent from '../../elements/TopBar/TopBarEvent';
import LoadDirectoryItem from '../../loaders/LoadDirectoryItem';
import LoadExhibitors from '../../loaders/LoadExhibitors';
import LoadParticipants from '../../loaders/LoadParticipants';
import SandboxedContent from '../../SandboxedContent/SandboxedContent';
import HasData from '../../validate/HasData';
import WaitWhileBusy from '../../WaitWhileBusy';
import Screen from '../Screen';
import CollectViewExhibitorScreen from './CollectViewExhibitorScreen';
import DirectorySearchBar from './DirectorySearchBar';
import Directory from './elements/Directory';
import DirectoryContentLinks from './elements/DirectoryContentLinks';
import DirectoryItemContacts from './elements/DirectoryItemContacts';
import DirectoryItemLinks from './elements/DirectoryItemLinks';
import FavoriteDirectoryItem from './elements/FavoriteDirectoryItem/FavoriteDirectoryItem';
import stylesheet from './EventDirectory.module.scss';
import EventDirectoryHeader from './EventDirectoryHeader/EventDirectoryHeader';
import EventDirectoryTags from './EventDirectoryTags/EventDirectoryTags';
import PresentationSession from './PresentationSession/PresentationSession';
import PresentationSpeaker from './PresentationSpeaker/PresentationSpeaker';

// NOTE: presentations > speaker uses speaker_id as primary key

const EventDirectoryItem = (props) => {
    const { eventId, itemId, parents } = props,
        [iframe, setIframe] = useState(),
        event = useEvent(eventId),
        item = useDirectoryItem(eventId, itemId),
        presentationSessions = useMemo(() => uniqBy(item.presentations?.map((p) => p.program) || [], 'id'), [item]),
        presentationSpeakers = useMemo(() => uniqBy(item.presentations?.map((p) => p.speaker) || [], 'speaker_id'), [item]),
        parent = useDirectoryItem(eventId, item.parent_id ?? last(parents ?? []));

    const handleOpenFilterScreen = useCallback(() => app.history.push('./filter'), []);

    // If empty but has an hyperlink, open it
    useEffect(() => {
        if (!itemHasContent(item, ['hyperlink']) && !(event.exhibitors_map_url && item.coordinates?.length) && item.hyperlink) {
            console.log('Looks like directory item only contains an hyperlink');
            openLink(item.hyperlink);
            if (!isLocalLink(item.hyperlink)) {
                app.history.goBack();
            }
        }
    }, [item]);

    // clear map center on leave
    useEffect(
        () => () => {
            app.store.dispatch(app.actions.ui.sidePanel.centerMap(null));
        },
        [item]
    );

    // Callbacks
    const handleShowMap = useCallback(() => {
        app.store.dispatch([
            // Show floorplan
            app.actions.ui.sidePanel.showFloorplan(true),
            // Center at coordinates
            app.actions.ui.sidePanel.centerMap({ ...item.coordinates }),
        ]);
    }, [item]);
    const handleOpenContentLink = useCallback((content) => {
        if (content.openpopup) {
            setIframe({
                url: content.link,
                key: new Date().toISOString(),
                title: content.title,
            });
        } else {
            openLink(content.link);
        }
    }, []);
    const handleCloseModal = useCallback(() => setIframe(null), []);

    // Output screen
    const { exhibitors_map_url } = event,
        mapIcon = <Icon src="/img/event_map.png" color="white" />,
        icons = [
            'is_favorite' in item ? <FavoriteDirectoryItem key="favorite" eventId={eventId} item={item} /> : null,
            exhibitors_map_url && item.coordinates?.length ? <MaximizeToolbarIcon icon={mapIcon} key="maximize-toolbar" /> : null,
        ].filter(identity),
        header = (
            <div>
                <TopBarEvent event={event} returnURL=".." icons={icons.length ? icons : null}>
                    {parent?.name ?? item.name}
                </TopBarEvent>
                <DirectorySearchBar event={event} item={item} onOpenFilterScreen={handleOpenFilterScreen} />
            </div>
        ),
        footer = <div />;

    console.log('DBG parent', parent);

    return (
        <>
            <Screen header={header} footer={footer}>
                <CollectViewExhibitorScreen item={item} />
                <Scrollable>
                    <EventDirectoryHeader item={item} parent={parent} />
                    <ul className={['list', stylesheet.presentations].join(' ')}>
                        {presentationSessions.map((session) => (
                            <PresentationSession event={event} session={session} key={session.id} />
                        ))}
                        {presentationSpeakers.map((speaker) => (
                            <PresentationSpeaker event={event} speaker={speaker} key={speaker.speaker_id} />
                        ))}
                    </ul>

                    {item.description ? (
                        <div className="content">
                            <SandboxedContent className="marginTop marginBottom" content={item.description} fitHeightToContents noInnerPadding />
                        </div>
                    ) : null}
                    {item.tags?.length ? (
                        <div className="content">
                            <EventDirectoryTags event={event} item={item} />
                        </div>
                    ) : null}

                    <DirectoryItemLinks event={event} item={item} onShowMap={handleShowMap} />
                    <DirectoryContentLinks event={event} item={item} onOpenContentLink={handleOpenContentLink} />
                    <DirectoryItemContacts event={event} item={item} />

                    {item.is_directory ? <Directory eventId={eventId} itemId={itemId} /> : null}
                </Scrollable>
            </Screen>
            <Modal show={!!iframe} onClose={handleCloseModal} header={<h2 style={{ color: event.settings.primary_color }}>{iframe?.title}</h2>} fullHeight>
                <iframe
                    key={iframe?.key}
                    src={iframe?.url || ''}
                    style={{ height: '100%' }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen></iframe>
            </Modal>
        </>
    );
};

export default (props) => {
    const params = useParams(),
        { eventId, path: fullPath } = params,
        path = (fullPath || '').split('/'),
        items = path.map((n) => parseInt(n)),
        itemId = parseInt(last(path)),
        parents = slice(path, 0, -1) || [],
        deduplicatedPath = path.filter((itemId, index) => {
            if (index == 0) return true;
            return path[index - 1] != itemId;
        }),
        match = useRouteMatch();

    if (deduplicatedPath.length != path.length) {
        const newPath = generatePath(match.path, { ...params, path: deduplicatedPath });
        return <Redirect to={newPath} />;
    }

    // Exhibitors loaded just for the tags
    return (
        <LoadDirectoryItem eventId={props.match.params.eventId} items={items} key={path.join('/')}>
            <LoadParticipants eventId={props.match.params.eventId}>
                <LoadExhibitors eventId={props.match.params.eventId}>
                    <WaitWhileBusy>
                        <HasData path={`events[${props.match.params.eventId}].directory.all[${itemId}]`}>
                            <EventDirectoryItem key={itemId} {...props.match.params} itemId={itemId} parents={parents} />
                        </HasData>
                    </WaitWhileBusy>
                </LoadExhibitors>
            </LoadParticipants>
        </LoadDirectoryItem>
    );
};
